import { isArray, isNull, isString } from "lodash";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Box } from "@mui/material";
import { t } from "i18next";
import he from "he";

import { formatCommonDates } from "utils";
import { BadgeSwitch } from "components";
import {
  getFontFamily,
  getPolicies,
  setJaiEditUrl,
  selectPropertyId,
  selectPropertyName,
  selectIsJAI,
  getJaiEditUrl,
  getBadgeSwitchItems,
  getSelectedBadgeItem,
  setSelectedBadgeItemId,
} from "slices";
import Neighborhood from "components/MessagesList/Message/Neighborhood";

import ExtraOption from "../ExtraOptions";

const FeaturesView = ({ showButtons = true }) => {
  const dispatch = useDispatch();

  const policies = useSelector(getPolicies);
  const fontFamily = useSelector(getFontFamily);
  const propertyName = useSelector(selectPropertyName);
  const propertyId = useSelector(selectPropertyId);
  const isJAI = useSelector(selectIsJAI);
  const jaiEditURL = useSelector(getJaiEditUrl);
  const badgeItems = useSelector(getBadgeSwitchItems);
  const selectedBadgeItem = useSelector(getSelectedBadgeItem);

  const [policyData, setPolicyData] = useState(null);

  const configureEditUrl = () => {
    const { id } = selectedBadgeItem;

    switch (id) {
      case "overview":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/info#hygroup_info2`));
        break;
      case "features":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/amenities`));
        break;
      case "amenities":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/amenities#hygroup_amenities`));
        break;
      case "pet":
      case "parking":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/amenities#hygroup_pet_parking`));
        break;
      case "neighborhood":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/amenities#hygroup_neighorhood`));
        break;
      case "hours":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/info`));
        break;
      case "offer":
        dispatch(setJaiEditUrl(`${process.env.REACT_APP_CRM_BASE_URL}/sites/${propertyId}/info#hygroup_promotion`));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!isNull(policies) && !isNull(selectedBadgeItem)) {
      setPolicyData(policies[selectedBadgeItem.label]);
      configureEditUrl();
    }
  }, [policies, selectedBadgeItem]);

  const clearBrowserSelection = () => {
    if (window.getSelection) {
      const selection = window.getSelection();
      selection.removeAllRanges();
    } else if (document.selection) {
      document.selection.empty();
    }
  };

  const extraButtons = isJAI
    ? [
        {
          text: t("editSubOrg"),
          handleClick: () => window.open(jaiEditURL, "_blank"),
        },
      ]
    : null;

  return (
    <>
      <div className="hayley-flex hayley-w-full hayley-flex-col hayley-justify-between">
        <div className="hayley-h-fit">
          <BadgeSwitch
            module="community info"
            disabled={false}
            badgeItems={badgeItems.filter((i) => i.id != "offer")}
            setSelectedItem={(id) => dispatch(setSelectedBadgeItemId(id))}
            selectedItem={selectedBadgeItem}
            clearBrowserSelection={clearBrowserSelection}
          />
        </div>

        <div key={selectedBadgeItem?.id} data-testid={`communityInfo_${selectedBadgeItem?.id}`}>
          {isArray(policyData?.data) && policyData ? (
            <Box
              className="hayley-custom hayley-mt-[18px] hayley-space-y-3 hayley-text-sm hayley-text-secondary"
              sx={{ "&.hayley-custom": { fontFamily: `${fontFamily} !important` } }}
            >
              <div className="hayley-font-semibold">{he.decode(policyData.title)}</div>
              <ul>
                {policyData.data.map((d, idx) => {
                  if (policyData.label !== t("officeHours")) {
                    return <li key={idx}>{he.decode(d)}</li>;
                  }

                  const [day, hours] = d.split(", ");

                  return <li key={idx}>{he.decode(`${day}, ${hours.trim() === "-" ? "Closed - Closed" : hours.trim()}`)}</li>;
                })}
              </ul>
            </Box>
          ) : isString(policyData?.data) ? (
            <Box
              className="hayley-custom hayley-mt-[18px]  hayley-space-y-3 hayley-text-sm hayley-text-secondary"
              sx={{ "&.hayley-custom": { fontFamily: `${fontFamily} !important` } }}
            >
              <div className="hayley-font-semibold">{he.decode(propertyName)}</div>
              <span>{he.decode(policyData.data)}</span>
            </Box>
          ) : policyData ? (
            <>
              {[t("features"), t("amenities")].includes(policyData?.label) ? (
                Object.entries(policyData?.data || {})
                  ?.sort((a, b) => {
                    let [aName] = a;
                    let [bName] = b;

                    if (aName === "Other") {
                      return 1;
                    }

                    if (bName === "Other") {
                      return -1;
                    }

                    return 0;
                  })
                  .map(([title, items]) => (
                    <div key={title}>
                      <div
                        className="hayley-mb-[12px] hayley-px-[12px] hayley-pb-[8px] hayley-pt-[12px] hayley-text-[14px] hayley-font-semibold hayley-text-[#333333]"
                        style={{ borderBottom: "1px solid #EEEEEE" }}
                      >
                        {he.decode(title)}
                      </div>

                      <ul className="hayley-my-[12px] hayley-grid hayley-w-full hayley-list-none hayley-grid-cols-2 hayley-gap-x-[16px] hayley-gap-y-[12px] hayley-pl-0">
                        {items.map((item, index) => (
                          <li className="hayley-flex hayley-gap-x-[8px] hayley-pl-[8px]" key={index}>
                            <span className="hayley-pt-[1px]">
                              <svg width={20} height={20} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                  d="M16.7357 5.76437L15.7357 4.76438C15.6054 4.63404 15.3947 4.63404 15.2644 4.76438L7.83337 12.1954L4.73571 9.09771C4.60538 8.96737 4.39471 8.96737 4.26438 9.09771L3.26438 10.0977C3.13404 10.228 3.13404 10.4387 3.26438 10.569L7.59771 14.9024C7.66271 14.9674 7.74804 15 7.83337 15C7.91871 15 8.00404 14.9674 8.06904 14.9024L16.7357 6.23571C16.866 6.10537 16.866 5.89471 16.7357 5.76437Z"
                                  fill="url(#paint0_linear_10413_13173)"
                                />
                                <defs>
                                  <linearGradient id="paint0_linear_10413_13173" x1={10} y1="4.66663" x2={10} y2={15} gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#6AD084" />
                                    <stop offset={1} stopColor="#3CB34B" />
                                  </linearGradient>
                                </defs>
                              </svg>
                            </span>
                            <span className="hayley-flex-1 hayley-text-[14px] hayley-text-[#333333]">{he.decode(item)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))
              ) : policyData?.label === "Neighborhood" ? (
                <div className="">
                  <Neighborhood message={policyData?.data} />
                </div>
              ) : (
                <Offer data={policyData.data} title={policyData.title} fontFamily={fontFamily} />
              )}
            </>
          ) : null}
        </div>

        {showButtons ? <ExtraOption from="community_info" extraButtons={extraButtons} /> : null}
      </div>
    </>
  );
};

export const Offer = ({ fontFamily = "ralewayregular, 'Open Sans', sans-serif", title, data = {}, isSingleView = false, showButtons = false }) => {
  let endDate = data?.end_date || data?.dynamic_end_date_text;

  const validFormat = formatCommonDates(endDate);

  return (
    <Box
      className={`hayley-custom hayley-mt-[18px]  hayley-space-y-3 hayley-text-sm hayley-text-secondary  ${showButtons ? "!hayley-mb-5" : ""}  ${
        isSingleView ? " hayley-pl-12" : ""
      }`}
      sx={{ "&.hayley-custom": { fontFamily: `${fontFamily} !important` } }}
    >
      {/* <div className="hayley-font-semibold">{title}</div> */}
      <p>{data?.description}</p>
      {validFormat ? <p>Ends on {validFormat}</p> : null}
      {data?.disclaimer ? (
        <>
          <p className=" hayley-font-semibold">Disclaimer:</p>
          <p>{data?.disclaimer}</p>
        </>
      ) : null}

      {showButtons ? <ExtraOption from="community_info" extraButtons={null} /> : null}
    </Box>
  );
};

export default FeaturesView;
